import React, { useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const DIGITS = [
  [1, 2, 3, 4, 5],
  [6, 7, 8, 9, 0],
];
const POINTS_ERROR = "Points must be between 1 to 10000";

function readError(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Unable to load suggestions."
  );
}

export default function MatkaSinglePanaBulk({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [enterPoints, setEnterPoints] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [bids, setBids] = useState([]);
  const [pointsError, setPointsError] = useState("");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });
  const requestId = useRef(0);

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.pana).map(
        (bid) => ({
          ...bid,
          id: `${bid.pana}-${nextGameType}`,
        })
      )
    );
  };

  const handlePointsChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 5);
    setEnterPoints(next);
    if (!next) {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const handleKeypadClick = async (digit) => {
    if (loading) return;

    const pointsValue = parseInt(enterPoints, 10);
    if (
      !enterPoints.trim() ||
      !Number.isInteger(pointsValue) ||
      pointsValue < 1 ||
      pointsValue > 10000
    ) {
      setPointsError(POINTS_ERROR);
      return;
    }

    setSelectedKey(digit);
    setPointsError("");
    setFormError("");
    const session = gameType;
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    try {
      const data = await fetchMatkaSuggestions("single_pana_bulk", String(digit));
      if (id !== requestId.current) return;
      const panas = Array.isArray(data?.panas) ? data.panas.map(String) : [];
      if (!panas.length) {
        setFormError("No suggestions available. Please try again.");
        return;
      }

      setBids((current) => {
        const next = [...current];
        panas.forEach((pana) => {
          const index = next.findIndex(
            (bid) => bid.pana === pana && bid.gameType === session
          );
          if (index >= 0) {
            const existing = next.splice(index, 1)[0];
            next.unshift({
              ...existing,
              points: Number(existing.points) + pointsValue,
            });
            return;
          }
          next.unshift({
            id: `${pana}-${session}`,
            pana,
            points: pointsValue,
            gameType: session,
          });
        });
        return next;
      });
      setEnterPoints("");
      setSelectedKey(null);
    } catch (error) {
      if (id !== requestId.current) return;
      setFormError(readError(error));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const handleDeleteBid = (bidId) => {
    setBids((current) => current.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Single Panna Bulk</h1>

          {/* Two-Column Form: GAME TYPE & ENTER POINTS */}
          <div className="row g-3 mb-4">
            {/* Game Type */}
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">GAME TYPE</label>
              <MatkaSessionSelect
                  betDirection={selectedGameType?.betDirection}
                  value={gameType}
                  onChange={handleGameTypeChange}
                  variant="bulk"
                />
            </div>

            {/* Enter Points */}
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">ENTER POINTS</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="Enter Points (e.g. 100)"
                value={enterPoints}
                onChange={handlePointsChange}
              />
              {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
            </div>
          </div>

          {/* Select Target Digits Card */}
          <div className="matka-target-digits-card mb-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
              <h2 className="matka-target-card-title mb-0">
                Select Target Digits
              </h2>
              <span className="matka-target-card-subtitle">
                Pick digits below to generate Single Panna combinations
              </span>
            </div>

            <div className="matka-target-digits-grid">
              {DIGITS.map((row, rIdx) => (
                <div key={rIdx} className="matka-target-digits-row">
                  {row.map((digit) => (
                    <button
                      key={digit}
                      type="button"
                      className={`matka-target-digit-btn ${
                        selectedKey === digit ? "active" : ""
                      }`}
                      onClick={() => handleKeypadClick(digit)}
                      disabled={loading}
                    >
                      {digit}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {formError && <p className="matka-sangam-error mb-3">{formError}</p>}

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Panna Digit</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.pana}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.pana}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
